/**
 * Utilidad para obtener y calcular el status de shipping
 * 
 * ⚠️ IMPORTANTE: El status YA VIENE CALCULADO desde el backend.
 * El backend calcula y guarda el campo `status` automáticamente cuando se guarda un shipping.
 * 
 * Estas funciones usan el campo `status` del backend cuando está disponible.
 * Solo calculan el status localmente cuando:
 * - El campo `status` no está presente (casos edge)
 * - Se está editando un shipping y se quiere ver el status antes de guardar
 * 
 * ⚠️ La función calculateShippingStatus debe mantenerse IDÉNTICA a la del backend:
 * back/hooks/hook_library/hook_library_shipping.js -> calculateShippingStatus
 * 
 * También existe un endpoint en el backend para recalcular:
 * POST /api/gotrading/shipping/calculate-status con { item: {...} } en el body
 * O GET /api/gotrading/shipping/:shippingId/calculate-status
 */

/**
 * Calcula el status de un shipping basado en sus fechas
 * 
 * ⚠️ NOTA: Esta función solo debe usarse cuando:
 * - El campo `status` no viene del backend (casos edge)
 * - Se está editando un shipping y se quiere ver el status antes de guardar
 * 
 * En la mayoría de los casos, el status ya viene calculado desde el backend
 * y debe usarse directamente: `item.status`
 * 
 * @param {Object} item - El item de shipping con sus fechas
 * @returns {number} - Prioridad del status (1-6)
 * 
 * Status codes:
 * 1 - Gris (por defecto)
 * 2 - Amarillo (operación pendiente)
 * 3 - Naranja (operación atrasada)
 * 4 - Azul (fecha de salida alcanzada)
 * 5 - Rojo (llegada próxima - 20 días o menos)
 * 6 - Verde (en puerto)
 * 
 * ⚠️ MANTENER SINCRONIZADO con back/hooks/hook_library/hook_library_shipping.js
 */

const TWENTY_DAYS_MS = 20 * 24 * 60 * 60 * 1000;

/**
 * Status «Upcoming ETA» (5), el que se muestra en rojo: la llegada está a 20 días o
 * menos y la operación todavía no salió del puerto.
 */
export const SHIPPING_STATUS_UPCOMING_ETA = 5;

/**
 * Status final de un embarque: 6 = «Gate out» (en puerto). Es el estado que cierra
 * la operación, así que sirve para saber si algo vencido sigue pendiente o no.
 */
export const SHIPPING_STATUS_GATE_OUT = 6;

/**
 * Por debajo de esto el pendiente de cobro se considera cero (redondeos de
 * centavos), igual que la tolerancia del accordion de finanzas.
 */
const CLIENT_PENDING_ZERO_TOLERANCE = 0.01;

/**
 * Clase que marca un embarque sin pendiente de cobro al cliente (ticket #424).
 * Solo cambia el color del texto: el fondo sigue siendo el status operativo.
 * Los colores viven en `assets/scss/md/_shipping-status.scss`.
 */
export const SHIPPING_COLLECTED_CLASS = 'status-collected';

/**
 * ¿El cliente ya pagó todo el embarque?
 *
 * Ticket #424: en el dashboard y en el reporte «Shipping» un embarque se ve igual
 * esté cobrado o no, sea cual sea su status, y lo que el usuario necesita
 * distinguir es a qué cliente hay que ir a reclamarle el pago.
 *
 * Usa `total_remaining` («Total restante a recibir»), que es el mismo pendiente
 * que muestra el accordion de finanzas y nunca queda negativo si se cobró de más.
 * Sin el campo (tenant que no lo tiene, o listado que no lo pidió) devuelve
 * `false`: ante la duda el embarque se sigue mostrando como pendiente.
 *
 * @param {Object} item - El item de shipping
 * @returns {boolean}
 */
export const isShippingClientPendingSettled = (item) => {
  if (!item || item.total_remaining === undefined || item.total_remaining === null || item.total_remaining === '') {
    return false;
  }
  const pending = Number(item.total_remaining);
  return Number.isFinite(pending) && pending <= CLIENT_PENDING_ZERO_TOLERANCE;
};

function resolveShippingEta(item) {
  if (item.eta != null && item.eta !== '') return item.eta;
  if (item.field_eta != null && item.field_eta !== '') return item.field_eta;
  return null;
}

export const calculateShippingStatus = (item) => {
  try {
    
    // Si no es un shipping o no tiene fechas, retornar prioridad por defecto
    if (!item || typeof item !== 'object') {
      return 1; // gris
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Resetear a inicio del día
    
    let priority = 1; // gris por defecto

    // Verificar fecha de inicio de operación (shipping_date)
    if (item.shipping_date) {
      const shippingDate = new Date(item.shipping_date);
      shippingDate.setHours(0, 0, 0, 0);
      
      if (shippingDate < today) {
        priority = 3; // naranja - operación atrasada
      } else {
        priority = 2; // amarillo - operación pendiente
      }
    }

    // Verificar fecha ETD (Estimated Time of Departure)
    if (item.etd) {
      const etdDate = new Date(item.etd);
      etdDate.setHours(0, 0, 0, 0);
      
      if (today >= etdDate) {
        priority = 4; // azul - fecha de salida alcanzada
      }
    }

    // Verificar fecha ETA (misma fuente que el cron: eta o field_eta)
    const effectiveEta = resolveShippingEta(item);
    if (effectiveEta) {
      const etaDate = new Date(effectiveEta);
      etaDate.setHours(0, 0, 0, 0);
      const etaWarningDate = new Date(etaDate.getTime() - TWENTY_DAYS_MS);
      if (today >= etaWarningDate) {
        priority = 5; // rojo - llegada próxima (20 días o menos)
      }
    }

    // Verificar si está en puerto (gate)
    if (item.gate_out) {
      priority = 6; // verde - en puerto
      return priority; // Prioridad máxima, no se puede superar
    }

    return priority;
  } catch (error) {
    console.error('Error calculating shipping status:', error);
    return 3; // naranja por defecto en caso de error
  }
};

/**
 * Status de un item de shipping como número.
 *
 * Usa el `status` que ya calculó y guardó el backend, y solo lo calcula localmente
 * si no vino (casos edge o un item en edición todavía sin guardar). El resto de los
 * helpers de este archivo pasan por acá para no repetir el mismo ternario.
 *
 * @param {Object} item - El item de shipping
 * @returns {number} - Prioridad del status (1-6)
 */
export const getShippingStatus = (item) => {
  if (item && item.status !== undefined && item.status !== null) {
    return Number(item.status);
  }
  return calculateShippingStatus(item);
};

/**
 * Obtiene la clase CSS para el status
 * @param {Object} item - El item de shipping
 * @returns {string} - Clase CSS para el status
 *
 * ⚠️ Usa el campo `status` del backend si está disponible
 */
export const getShippingStatusClass = (item) => getStatusClass(getShippingStatus(item));

/**
 * Color de cada status (1-6).
 *
 * Son exactamente los valores que ya usa el badge de los listados/reportes
 * (`components/gotrading/common/ShippingStatusBadge.vue`). Están acá para que el
 * dashboard pinte con la misma paleta que el reporte en lugar de tener la suya:
 * el ticket #365 se abrió justamente porque el ETA se veía celeste en el dashboard
 * y rojo en el reporte. Si hay que cambiar un color, se cambia acá.
 */
export const STATUS_COLORS = {
  1: { background: '#6c757d', text: '#ffffff' }, // gris  - To be coordinated
  2: { background: '#ffc107', text: '#212529' }, // amarillo - Coordinated
  3: { background: '#fd7e14', text: '#ffffff' }, // naranja - Consolidated
  4: { background: '#0d6efd', text: '#ffffff' }, // azul  - On board
  5: { background: '#dc3545', text: '#ffffff' }, // rojo  - Upcoming ETA
  6: { background: '#4ddf4d', text: '#212529' }, // verde - Gate out
};

/**
 * Color de fondo/texto para un número de status, con el gris como default.
 * @param {number} status - Número del status (1-6)
 * @returns {{background: string, text: string}}
 */
export const getStatusColors = (status) => {
  return STATUS_COLORS[status] || STATUS_COLORS[1];
};

/**
 * Colores del status de un item de shipping (usa `status` del backend y cae al
 * cálculo local solo si no vino, igual que `getShippingStatusClass`).
 * @param {Object} item - El item de shipping
 * @returns {{background: string, text: string}}
 */
export const getShippingStatusColors = (item) => getStatusColors(getShippingStatus(item));

/**
 * Color de fondo del status de un item de shipping.
 * @param {Object} item - El item de shipping
 * @returns {string} - Color hex
 */
export const getShippingStatusColor = (item) => getShippingStatusColors(item).background;

/**
 * Obtiene la clase CSS basada en el número de status
 * @param {number} status - Número del status (1-6)
 * @returns {string} - Clase CSS
 */
export const getStatusClass = (status) => {
  switch (status) {
    case 1:
      return 'status-grey';
    case 2:
      return 'status-yellow';
    case 3:
      return 'status-orange';
    case 4:
      return 'status-blue';
    case 5:
      return 'status-red';
    case 6:
      return 'status-green';
    default:
      return 'status-grey';
  }
};

/**
 * Obtiene el label del status basado en el número
 * @param {number} status - Número del status (1-6)
 * @param {Function} t - Función de traducción de i18n
 * @returns {string} - Label del status
 */
export const getStatusLabel = (status, t) => {
    switch (status) {
        case 1: return 'To be coordinated'; //gris
        case 2: return 'Coordinated'; //amarillo
        case 3: return 'Consolidated'; //naranja
        case 4: return 'On board'; //azul
        case 5: return 'Upcoming ETA'; //rojo
        case 6: return 'Gate out'; //verde
        default: return 'Unknown';
      }

};

/**
 * Obtiene el label del status basado en el item de shipping
 * @param {Object} item - El item de shipping
 * @param {Function} t - Función de traducción de i18n
 * @returns {string} - Label del status
 * 
 * ⚠️ Usa el campo `status` del backend si está disponible
 */
export const getShippingStatusLabel = (item, t) => getStatusLabel(getShippingStatus(item), t);

/**
 * Calcula el status usando el endpoint del backend (recomendado para garantizar consistencia)
 * Tiene fallback a la función local si el endpoint no está disponible
 * @param {Object} item - El item de shipping con sus fechas
 * @param {string} shippingId - ID del shipping (opcional, si no se proporciona item)
 * @returns {Promise<number>} - Prioridad del status (1-6)
 */
export const calculateShippingStatusFromBackend = async (item = null, shippingId = null) => {
  try {
    const { wencesApi } = await import('@/api/wencesApi');
    
    if (item) {
      // Usar POST con el item completo
      const response = await wencesApi.post('/api/gotrading/shipping/calculate-status', { item });
      return response.data.status;
    } else if (shippingId) {
      // Usar GET con el ID
      const response = await wencesApi.get(`/api/gotrading/shipping/${shippingId}/calculate-status`);
      return response.data.status;
    } else {
      throw new Error('Either item or shippingId must be provided');
    }
  } catch (error) {
    console.warn('Error calling backend status endpoint, using local calculation:', error);
    // Fallback a la función local
    if (item) {
      return calculateShippingStatus(item);
    }
    throw error;
  }
};

/**
 * Obtiene información completa del status de shipping
 * @param {Object} item - El item de shipping
 * @param {Function} t - Función de traducción de i18n
 * @returns {Object} - Objeto con status, clase, label y descripción
 * 
 * ⚠️ IMPORTANTE: Usa el campo `status` que viene del backend si está disponible.
 * Solo calcula el status si no está presente (ej: cuando se está editando antes de guardar).
 */
export const getShippingStatusInfo = (item, t) => {
  const status = getShippingStatus(item);


  const statusInfo = {
    1: { 
      class: 'status-grey', 
      label:'To be coordinated', 
      description: 'Default status' 
    },
    2: { 
      class: 'status-yellow', 
      label: 'Coordinated', 
      description: 'Operation pending' 
    },
    3: { 
      class: 'status-orange', 
      label: 'Consolidated', 
      description: 'Operation delayed' 
    },
    4: { 
      class: 'status-blue', 
      label: 'On board', 
      description: 'Departure date reached' 
    },
    5: { 
      class: 'status-red', 
      label: 'Upcoming ETA', 
      description: 'Arrival imminent (20 days or less)' 
    },
    6: { 
      class: 'status-green', 
      label: 'Gate out', 
      description: 'In port' 
    }
  };

  return {
    status,
    class: statusInfo[status]?.class || 'status-grey',
    label: statusInfo[status]?.label || 'Unknown',
    description: statusInfo[status]?.description || 'Unknown status'
  };
};
